import React, { use, useEffect, useMemo, useState } from 'react';
import DataTable, {
  ConditionalStyles,
  type TableColumn,
  type TableProps,
} from 'react-data-table-component';
import { Card, CardBody, Col, Row } from 'reactstrap';
import CommonCardHeader from '../../../../../coreComponents/CommonCardHeader';
import FilterComponent from '../common/FilterComponent';
import { useTranslation } from 'react-i18next';

import { CustomLoader } from '../../../../uiKits/customLoader/CustomLoader';


export type AjaxSourcedDataProps<T> = Omit<TableProps<T>, 'data' | 'columns' | 'title'> & {
  data: T[];
  columns: TableColumn<T>[];
  title?: string;
  subTitle?: string;
  /** Set to true to show the `progressComponent` while loading */
  loading?: boolean;
  searchServer?: boolean;
  onSearch?: (value: string) => void;
  searchText?: string; // controlled input from parent (optional)
  debounceMs?: number;
  hasFilter?: boolean;
  conditionalRowStyles?: ConditionalStyles<T>[];
  selectorComponent?: React.ReactNode;
};

const AjaxSourcedData = <T extends Record<string, any>>({
  data,
  columns,
  title,
  subTitle,
  defaultSortFieldId,
  defaultSortAsc = true,

  // ✅ defaults for client mode
  pagination = true,
  paginationServer = false,
  paginationPerPage = 10,
  paginationRowsPerPageOptions = [10, 25, 50],
  paginationDefaultPage,
  paginationTotalRows,
  onChangePage,
  onChangeRowsPerPage,
  sortServer = false,
  onSort,
  searchServer = false,
  onSearch,
  searchText,
  debounceMs = 400,
  hasFilter = true,
  paginationComponentOptions,
  conditionalRowStyles = [],
  loading = false,
  selectorComponent,
  ...rest
}: AjaxSourcedDataProps<T>) => {
  const { t } = useTranslation();
  const [filterText, setFilterText] = useState('');
  const [debounced, setDebounced] = useState(filterText);
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState<T[]>([]);
  const [resetToggle, setResetToggle] = useState(false);
  useEffect(() => {
    if (!data || loading) {
      setPending(true);
      return;
    }
    if (data) {
      setPending(false);
      setRows(data);
    }
  }, [data, loading]);

  useEffect(() => {
    setFilterText(searchText ?? '');
  }, [searchText]);

  // debounce

  useEffect(() => {
    const id = setTimeout(() => setDebounced(filterText.trim()), debounceMs);
    return () => clearTimeout(id);
  }, [filterText, debounceMs]);

  // reset pagination to page 1 on new search
  useEffect(() => {
    if (paginationServer && searchServer) {
      onSearch?.(debounced);
      setResetToggle((x) => !x);
      // Optionally tell parent to go page 1 if it controls page externally
      // onChangePage?.(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]); // intentionally only on debounced changes

  const doClientFilter = !(paginationServer && searchServer);
  const filteredItems = useMemo(() => {
    if (!rows) return [];
    if (!doClientFilter) return rows;
    const needle = filterText.toLowerCase();
    return rows.filter((item) =>
      Object.values(item).some((v) =>
        String(v ?? '')
          .toLowerCase()
          .includes(needle),
      ),
    );
  }, [rows, doClientFilter, filterText]);

  const mergedPaginationOptions = useMemo(
    () => ({
      rowsPerPageText: t('global.rowsPerPageText'),
      rangeSeparatorText: t('global.rowsOf'),
      ...paginationComponentOptions,
    }),
    [paginationComponentOptions, t],
  );

  return (
    <Col sm="12">
      <Card>
        {title && (
          <CommonCardHeader
            tag
            headClass="pb-0 card-no-border"
            title={title ?? ''}
            span={[{ text: subTitle }]}
            tagClass="mb-3"
          />
        )}
        <CardBody>
          {hasFilter || selectorComponent ? (
            <Row>
              {selectorComponent && <Col sm={hasFilter ? 6 : 12}>{selectorComponent}</Col>}
              {hasFilter && (
                <Col sm={selectorComponent ? 6 : 12} className="d-flex justify-content-end">
                  <FilterComponent
                    onFilter={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFilterText(e.target.value)
                    }
                    filterText={filterText}
                  />
                </Col>
              )}
            </Row>
          ) : null}
          <div className={`table-responsive ${rest.className ?? ''}`}>
            <DataTable
              {...rest}
              className="custom-scrollbar"
              data={filteredItems}
              columns={columns}
              progressComponent={rest.progressComponent ?? <CustomLoader />}
              progressPending={rest.progressPending ?? pending}
              striped
              defaultSortFieldId={defaultSortFieldId}
              defaultSortAsc={defaultSortAsc}
              /* pagination (client by default; server when enabled) */
              pagination={pagination}
              paginationServer={paginationServer}
              paginationPerPage={paginationPerPage}
              paginationRowsPerPageOptions={paginationRowsPerPageOptions}
              paginationDefaultPage={paginationDefaultPage}
              paginationTotalRows={paginationTotalRows}
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              sortServer={sortServer}
              onSort={onSort}
              paginationResetDefaultPage={resetToggle}
              paginationComponentOptions={mergedPaginationOptions}
              conditionalRowStyles={conditionalRowStyles}
            />
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

export default AjaxSourcedData;
